import type { VSidebarItemProps } from "@verbleif/lib/src/components/Sidebar/VSidebarItem.type";

const CSS_COLOR_PATTERN = /^(?:#|rgba?\(|hsla?\()/i;

const HEX_TO_TAILWIND: Record<string, string> = {
  "#3b82f6": "text-blue-500 dark:text-blue-400",
  "#ef4444": "text-red-500 dark:text-red-400",
  "#f97316": "text-orange-500 dark:text-orange-400",
  "#4caf58": "text-primary-500 dark:text-primary-400",
  "#2e7d32": "text-primary-900 dark:text-primary-400",
  "#f44336": "text-report_status-closed",
};

export function isCssColor(color: string | null | undefined): color is string {
  return typeof color === "string" && CSS_COLOR_PATTERN.test(color.trim());
}

export function hexToViewIconColorClass(hex: string): string | null {
  return HEX_TO_TAILWIND[hex.trim().toLowerCase()] ?? null;
}

export function getColorClass(color: string | null | undefined, fallback = "text-gray-400"): string {
  if (!color || isCssColor(color)) {
    const mapped = color ? hexToViewIconColorClass(color) : null;
    if (mapped) {
      return mapped;
    }
    return fallback;
  }

  switch (color) {
    case "gray-800":
      return "text-gray-800 dark:text-gray-200";
    default:
      return `text-${color}`;
  }
}

/**
 * Hex values mapped to Tailwind use `iconColor`; other CSS colors use inline `customIconColor`.
 */
export function resolveSidebarIconColor(
  color: string | null | undefined,
  fallbackClass: string,
): Pick<VSidebarItemProps, "customIconColor" | "iconColor"> {
  if (!color) {
    return { customIconColor: null, iconColor: fallbackClass };
  }
  const trimmed = color.trim();
  if (isCssColor(trimmed)) {
    const mapped = hexToViewIconColorClass(trimmed);
    if (mapped) {
      return { customIconColor: null, iconColor: mapped };
    }
    return { customIconColor: trimmed, iconColor: undefined };
  }
  return { customIconColor: null, iconColor: getColorClass(trimmed, fallbackClass) };
}

export function viewIconPreviewClasses(color: string | null | undefined, muted: boolean, fallbackClass: string): {
  className: string
  style: Record<string, string> | undefined
} {
  const resolved = resolveSidebarIconColor(color, fallbackClass);
  const className = [
    resolved.iconColor ?? "text-gray-600 dark:text-gray-300",
    muted ? "opacity-50" : "",
  ].filter(Boolean).join(" ");
  const style = resolved.customIconColor ? { color: resolved.customIconColor } : undefined;
  return { className, style };
}
